import { format, formatRelative, isThisYear, isValid, parseISO } from "date-fns";
import type { DateFormat, TimeFormat } from "@/types/user";

const safeParseISO = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parsedDate = parseISO(dateStr);
  return isValid(parsedDate) ? parsedDate : null;
};

export const toRelativeDate = (dateStr: string | null) => {
  if (!dateStr) return "";
  const date = safeParseISO(dateStr);
  if (!date) return "";
  return formatRelative(date, new Date());
};

const DATE_FORMAT_MAP: Record<DateFormat, string> = {
  US: "MM/dd/yyyy",
  ISO: "yyyy-MM-dd",
  UK_EU: "dd/MM/yyyy",
  Verbose: "dd MMM yyyy",
};

const DATE_SHORT_FORMAT_MAP: Record<DateFormat, string> = {
  US: "MMM d, yyyy", // Jan 15, 2024
  ISO: "yyyy-MM-dd", // 2024-01-15
  UK_EU: "d MMM yyyy", // 15 Jan 2024
  Verbose: "dd MMM yyyy",
};

const DAY_MONTH_FORMAT_MAP: Record<DateFormat, string> = {
  US: "EEEE, MMMM d", // Monday, January 15
  ISO: "EEEE, yyyy-MM-dd", // Monday, 2024-01-15
  UK_EU: "EEEE, d MMMM", // Monday, 15 January
  Verbose: "dd MMM yyyy",
};

const DAY_MONTH_YEAR_FORMAT_MAP: Record<DateFormat, string> = {
  US: "EEEE, MMMM d, yyyy", // Monday, January 15, 2024
  ISO: "EEEE, yyyy-MM-dd", // Monday, 2024-01-15
  UK_EU: "EEEE, d MMMM yyyy", // Monday, 15 January 2024
  Verbose: "dd MMM yyyy",
};

const DETAILED_DATETIME_FORMAT_MAP: Record<DateFormat, string> = {
  US: "EEE, MMM d yyyy", // Mon, Jan 15 2024
  ISO: "EEE, yyyy-MM-dd", // Mon, 2024-01-15
  UK_EU: "EEE, d MMM yyyy", // Mon, 15 Jan 2024
  Verbose: "dd MMM yyyy",
};

const MONTH_YEAR_FORMAT_MAP: Record<DateFormat, string> = {
  US: "MM/yy", // 01/24
  ISO: "yyyy-MM", // 2024-01
  UK_EU: "MM/yy", // 01/24
  Verbose: "dd MMM yyyy",
};

const TIME_FORMAT_MAP: Record<TimeFormat, string> = {
  "12h": "h:mm a",
  "24h": "HH:mm",
};

const TIME_FORMAT_WITH_SECONDS_MAP: Record<TimeFormat, string> = {
  "12h": "h:mm:ss a",
  "24h": "HH:mm:ss",
};

const parseDate = (date: Date | string | number): Date | null => {
  if (date instanceof Date) return date;
  if (typeof date === "number") return new Date(date);
  if (typeof date === "string") return safeParseISO(date);
  return null;
};

export const formatDateWithLocale = (date: Date | string | number, dateFormat: DateFormat): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    return format(parsedDate, DATE_FORMAT_MAP[dateFormat]);
  } catch {
    return "";
  }
};

export const formatTimeWithLocale = (
  date: Date | string | number,
  timeFormat: TimeFormat,
  includeSeconds = false,
): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    return format(parsedDate, includeSeconds ? TIME_FORMAT_WITH_SECONDS_MAP[timeFormat] : TIME_FORMAT_MAP[timeFormat]);
  } catch {
    return "";
  }
};

export const formatDateTimeWithLocale = (
  date: Date | string | number,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  includeSeconds = false,
): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    const dateStr = format(parsedDate, DATE_FORMAT_MAP[dateFormat]);
    const timeStr = format(
      parsedDate,
      includeSeconds ? TIME_FORMAT_WITH_SECONDS_MAP[timeFormat] : TIME_FORMAT_MAP[timeFormat],
    );
    return `${dateStr}, ${timeStr}`;
  } catch {
    return "";
  }
};

export const formatDateShortWithLocale = (date: Date | string | number, dateFormat: DateFormat): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    return format(parsedDate, DATE_SHORT_FORMAT_MAP[dateFormat]);
  } catch {
    return "";
  }
};

export const formatDayName = (date: Date | string | number): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    return format(parsedDate, "EEEE");
  } catch {
    return "";
  }
};

export const formatDayMonthWithLocale = (date: Date | string | number, dateFormat: DateFormat): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    const formatStr = isThisYear(parsedDate) ? DAY_MONTH_FORMAT_MAP[dateFormat] : DAY_MONTH_YEAR_FORMAT_MAP[dateFormat];
    return format(parsedDate, formatStr);
  } catch {
    return "";
  }
};

export const formatDetailedDateTimeWithLocale = (
  date: Date | string | number,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    const dateStr = format(parsedDate, DETAILED_DATETIME_FORMAT_MAP[dateFormat]);
    const timeStr = format(parsedDate, TIME_FORMAT_MAP[timeFormat]);
    return `${dateStr} at ${timeStr}`;
  } catch {
    return "";
  }
};

export const formatMonthYearWithLocale = (date: Date | string | number, dateFormat: DateFormat): string => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return "";
  try {
    return format(parsedDate, MONTH_YEAR_FORMAT_MAP[dateFormat]);
  } catch {
    return "";
  }
};
